import { inject, Injectable } from '@angular/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { CapacitorService } from '../capacitor/capacitor.service';
import { LoggingService } from '../logging/logging.service';
import { ThemeService } from './theme.service';

// The toolbar surface behind the Android status bar. Mirrors
// --retro-surface-bar in src/theme/variables.scss (light, then dark): the
// native bar cannot read a CSS custom property, so the two move together.
const barBackground = {
  light: '#f4fbfd',
  dark: '#1a1a1a',
} as const;

@Injectable({
  providedIn: 'root',
})
export class StatusBarService {
  private capacitorService = inject(CapacitorService);
  private logger = inject(LoggingService);
  private themeService = inject(ThemeService);

  /**
   * Match the status bar to the current colour scheme and keep it matched as
   * the scheme changes. Native only; a no-op in the browser.
   */
  initialize(): void {
    if (!this.capacitorService.mobile) {
      return;
    }

    this.themeService.dark$.subscribe((dark) => void this.apply(dark));
  }

  private async apply(dark: boolean): Promise<void> {
    try {
      // Style names describe the bar's text, not the app: Dark is light text
      // for a dark background.
      await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });

      // iOS keeps the bar overlaying the web view, where Ionic's safe-area
      // handling already pads the header. Android has no such padding, so the
      // bar sits above the web view, painted as the toolbar surface.
      if (this.capacitorService.android) {
        await StatusBar.setBackgroundColor({
          color: dark ? barBackground.dark : barBackground.light,
        });
        await StatusBar.setOverlaysWebView({ overlay: false });
      }
    } catch (error) {
      this.logger.warn('[StatusBar] Failed to apply style:', error);
    }
  }
}
